import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  ArrowRight,
  ArrowLeft,
  MapPin,
  FileText,
  Ruler,
  X,
} from 'lucide-react';

import SeriesNav from './SeriesNav';
import '../pages/ActiveDeterrenceCamera.css';

/**
 * Shared detail-page template.
 *
 * Optional sections auto-hide when arrays are empty / undefined.
 * Back navigation, CTA wording, and SeriesNav can be customized per page.
 */
const SeriesTemplate = ({
  id,
  eyebrow,
  name,
  description,
  tags = [],
  heroImage,
  features = [],
  locations = [],
  models = [],
  commonBadges = [],
  compareRows = [],
  notes = [],
  ctaTitle,
  ctaBody,
  ctaButtonLabel = 'このシリーズについて相談する',
  compareColumns,

  // Navigation
  backTo = '/products/security-camera',
  backLabel = '防犯カメラ一覧へ戻る',
  showSeriesNav = true,

  // Optional section titles
  featuresTitle = 'このシリーズの特長',
  locationsTitle = '適した設置場所',
  modelsTitle = 'モデルラインアップ',
  compareTitle = '比較表',
  notesTitle = 'ご確認事項',
}) => {
  const [openDiagram, setOpenDiagram] = useState(null);

  useEffect(() => {
    if (!openDiagram) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpenDiagram(null);
      }
    };

    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [openDiagram]);

  const cols =
    compareColumns || [
      'シリーズ',
      '形状',
      'レンズ',
      '解像度',
      '照射距離',
      '向いている用途',
    ];

  return (
    <div
      className="adc-page"
      data-testid={`series-page-${id}`}
    >
      {/* ===== Hero ===== */}
      <section
        className="adc-hero"
        data-testid="adc-hero"
      >
        <div className="container">
          {showSeriesNav && (
            <SeriesNav currentId={id} />
          )}

          <div className="adc-hero-inner">
            <div className="adc-hero-body">
              <Link
                to={backTo}
                className="adc-back-link"
                data-testid="back-link-top"
              >
                <ArrowLeft
                  size={14}
                  strokeWidth={2}
                />
                {backLabel}
              </Link>

              <span className="adc-hero-eyebrow">
                <span
                  className="adc-hero-eyebrow-dot"
                  aria-hidden="true"
                />
                {eyebrow}
              </span>

              <h1 className="adc-hero-title">
                {name}
              </h1>

              <span
                className="adc-hero-divider"
                aria-hidden="true"
              />

              <p className="adc-hero-lead">
                {description}
              </p>

              {tags.length > 0 && (
                <ul
                  className="adc-hero-tags"
                  data-testid="hero-tags"
                >
                  {tags.map((t, i) => (
                    <li
                      key={i}
                      className={t.red ? 'is-red' : ''}
                    >
                      {typeof t === 'string'
                        ? t
                        : t.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div
              className="adc-hero-photo"
              data-testid="hero-photo"
            >
              {heroImage ? (
                <img
                  src={heroImage}
                  alt={`${name} 製品イメージ`}
                />
              ) : (
                <div className="adc-hero-photo-placeholder">
                  IMAGE
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Features ===== */}
      {features.length > 0 && (
        <section
          className="adc-section"
          id="features"
          data-testid="section-features"
        >
          <div className="container">
            <div className="adc-section-head">
              <span className="adc-eyebrow">
                FEATURES
              </span>

              <h2 className="adc-title">
                {featuresTitle}
              </h2>
            </div>

            <div className="adc-feature-grid">
              {features.map((f, i) => {
                const Icon = f.Icon;

                return (
                  <div
                    key={i}
                    className={
                      'adc-feature-card' +
                      (f.red ? ' is-red' : '')
                    }
                    data-testid={`feature-card-${i}`}
                  >
                    <div className="adc-feature-head">
                      <span className="adc-feature-icon">
                        {Icon ? (
                          <Icon
                            size={22}
                            strokeWidth={1.6}
                          />
                        ) : null}
                      </span>

                      <h3 className="adc-feature-title">
                        {f.title}
                      </h3>
                    </div>

                    <p className="adc-feature-desc">
                      {f.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ===== Locations ===== */}
      {locations.length > 0 && (
        <section
          className="adc-section adc-section-alt"
          id="locations"
          data-testid="section-locations"
        >
          <div className="container">
            <div className="adc-section-head">
              <span className="adc-eyebrow">
                SUITABLE LOCATIONS
              </span>

              <h2 className="adc-title">
                {locationsTitle}
              </h2>
            </div>

            <div className="adc-location-list">
              {locations.map((loc, i) => (
                <span
                  key={i}
                  className="adc-location-item"
                  data-testid={`location-item-${i}`}
                >
                  <span
                    className="adc-location-dot"
                    aria-hidden="true"
                  />

                  <MapPin
                    size={14}
                    strokeWidth={1.8}
                  />

                  {loc}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== Models ===== */}
      {models.length > 0 && (
        <section
          className="adc-section"
          id="models"
          data-testid="section-models"
        >
          <div className="container">
            <div className="adc-section-head">
              <span className="adc-eyebrow">
                MODEL LINEUP
              </span>

              <h2 className="adc-title">
                {modelsTitle}
              </h2>
            </div>

            <div className="adc-model-grid">
              {models.map((m, i) => (
                <div
                  key={i}
                  className="adc-model-card"
                  data-testid={`model-card-${i}`}
                >
                  <div className="adc-model-media">
                    <div className="adc-model-image">
                      {m.image ? (
                        <img
                          src={m.image}
                          alt={m.name}
                        />
                      ) : (
                        <div className="adc-model-image-placeholder">
                          IMAGE
                        </div>
                      )}
                    </div>

                    {m.modelList &&
                      m.modelList.length > 0 && (
                        <div
                          className="adc-model-modelnames"
                          data-testid={`model-list-${i}`}
                        >
                          <span className="adc-block-label">
                            対応モデル
                          </span>

                          <div className="adc-model-models">
                            {m.modelList.map(
                              (mm, j) => (
                                <span
                                  key={j}
                                  className="adc-model-tag"
                                >
                                  {mm}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                      )}

                    {m.diagram && (
                      <button
                        type="button"
                        className="adc-model-thumb"
                        onClick={() =>
                          setOpenDiagram(m)
                        }
                        data-testid={`diagram-open-${i}`}
                        aria-label={`${m.name} 外形寸法図を見る`}
                      >
                        <span className="adc-model-thumb-label">
                          <Ruler
                            size={12}
                            strokeWidth={2}
                          />
                          外形寸法図
                        </span>

                        <span className="adc-model-thumb-box">
                          <img
                            src={m.diagram}
                            alt={`${m.name} 外形寸法図`}
                            loading="lazy"
                          />

                          <span className="adc-model-thumb-cta">
                            クリックで拡大
                            <ArrowRight
                              size={11}
                              strokeWidth={2.2}
                            />
                          </span>
                        </span>
                      </button>
                    )}
                  </div>

                  <div className="adc-model-body">
                    <div className="adc-model-head">
                      <h3
                        className="adc-model-name"
                        data-testid={`model-name-${i}`}
                      >
                        {m.name}
                      </h3>

                      {m.type && (
                        <span className="adc-model-type">
                          {m.type}
                        </span>
                      )}
                    </div>

                    {m.desc && (
                      <p className="adc-model-desc">
                        {m.desc}
                      </p>
                    )}

                    {commonBadges.length > 0 && (
                      <div>
                        <p className="adc-block-label">
                          主な機能
                        </p>

                        <div
                          className="adc-badge-row"
                          data-testid={`feature-badges-${i}`}
                        >
                          {commonBadges.map(
                            (
                              { Icon, label },
                              k
                            ) => (
                              <span
                                key={k}
                                className="adc-badge"
                              >
                                {Icon ? (
                                  <Icon
                                    size={13}
                                    strokeWidth={1.8}
                                  />
                                ) : null}

                                {label}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {m.specs &&
                      m.specs.length > 0 && (
                        <div>
                          <p className="adc-block-label">
                            主要仕様
                          </p>

                          <ul className="adc-spec-list">
                            {m.specs.map(
                              (s, j) => (
                                <li key={j}>
                                  {s}
                                </li>
                              )
                            )}
                          </ul>
                        </div>
                      )}

                    <div className="adc-model-actions">
                      <span
                        className="adc-btn-primary"
                        aria-disabled="true"
                        data-testid={`pdf-btn-${i}`}
                        title="仕様書は準備中です"
                      >
                        <FileText
                          size={16}
                          strokeWidth={2}
                        />
                        仕様書PDF
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== Comparison ===== */}
      {compareRows.length > 0 && (
        <section
          className="adc-section adc-section-alt"
          id="compare"
          data-testid="section-compare"
        >
          <div className="container">
            <div className="adc-section-head">
              <span className="adc-eyebrow">
                COMPARISON
              </span>

              <h2 className="adc-title">
                {compareTitle}
              </h2>
            </div>

            <div className="adc-compare-wrap">
              <table
                className="adc-compare-table"
                data-testid="compare-table"
              >
                <thead>
                  <tr>
                    {cols.map((c, i) => (
                      <th key={i}>
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {compareRows.map((r, i) => (
                    <tr
                      key={i}
                      data-testid={`compare-row-${i}`}
                    >
                      {r.map((cell, j) => (
                        <td key={j}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* ===== Notes ===== */}
      {notes.length > 0 && (
        <section
          className="adc-section"
          id="notes"
          data-testid="section-notes"
        >
          <div className="container">
            <div className="adc-section-head">
              <span className="adc-eyebrow">
                NOTES
              </span>

              <h2 className="adc-title">
                {notesTitle}
              </h2>
            </div>

            <div className="adc-notes-card">
              <ul className="adc-notes-list">
                {notes.map((n, i) => (
                  <li
                    key={i}
                    data-testid={`note-${i}`}
                  >
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* ===== CTA ===== */}
      <section
        className="adc-cta"
        data-testid="section-cta"
      >
        <div className="container">
          <div className="adc-cta-card">
            <span className="adc-cta-eyebrow">
              CONTACT
            </span>

            <h2 className="adc-cta-title">
              {ctaTitle ||
                `${name}についてご相談ください`}
            </h2>

            <p className="adc-cta-body">
              {ctaBody ||
                '設置場所や目的に合わせて、カメラ本体・録画機器・周辺機器の構成をご案内いたします。'}
            </p>

            <Link
              to="/contact"
              className="adc-cta-btn"
              data-testid="cta-btn"
            >
              {ctaButtonLabel}

              <ArrowRight
                size={18}
                strokeWidth={2}
              />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== Bottom back link ===== */}
      <section
        className="adc-bottom-nav"
        data-testid="section-bottom-nav"
      >
        <div className="container">
          <Link
            to={backTo}
            className="adc-bottom-back"
            data-testid="back-link-bottom"
          >
            <ArrowLeft
              size={16}
              strokeWidth={2}
            />

            {backLabel}
          </Link>
        </div>
      </section>

      {/* ===== 外形寸法図 lightbox ===== */}
      {openDiagram && (
        <div
          className="adc-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`${openDiagram.name} 外形寸法図`}
          onClick={() =>
            setOpenDiagram(null)
          }
          data-testid="diagram-lightbox"
        >
          <div
            className="adc-lightbox-panel"
            onClick={(e) =>
              e.stopPropagation()
            }
          >
            <div className="adc-lightbox-head">
              <div className="adc-lightbox-title">
                <span className="adc-block-label">
                  外形寸法図
                </span>

                <h4>
                  {openDiagram.name}
                </h4>
              </div>

              <button
                type="button"
                className="adc-lightbox-close"
                onClick={() =>
                  setOpenDiagram(null)
                }
                aria-label="閉じる"
                data-testid="diagram-close"
              >
                <X
                  size={18}
                  strokeWidth={2}
                />
              </button>
            </div>

            <div className="adc-lightbox-body">
              <img
                src={openDiagram.diagram}
                alt={`${openDiagram.name} 外形寸法図`}
                className="adc-lightbox-img"
              />
            </div>

            <p className="adc-lightbox-note">
              ※図中の寸法単位はmmです。設置環境により実際の取付位置は異なる場合があります。
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeriesTemplate;